import { Checkbox } from '@carbon/react';
import { Row, Table } from '@tanstack/react-table';
import { useDataTableContext } from './context';

// ─────────────────────────────────────
// Cell component (per-row checkbox)
// ─────────────────────────────────────

interface SelectCheckboxCellProps<TData> {
  row: Row<TData>;
  tableUid: string;
  hasSelectionLimit: boolean;
  maxSelections?: number;
}

/**
 * Per-row checkbox cell for the auto-injected `_select` column.
 * Reads selection state from DataTableContext so the parent column def can
 * stay stable across selection changes (no useMemo invalidation, no remount).
 *
 * @param {SelectCheckboxCellProps} props - Component props
 * @param {Row} props.row - TanStack row instance
 * @param {string} props.tableUid - Unique per-DataTable instance prefix for the checkbox `id`
 * @param {boolean} props.hasSelectionLimit - Whether `maxSelections` is set
 * @param {number} [props.maxSelections] - Maximum selectable rows when limit is enforced
 * @return {JSX.Element} The cell checkbox element
 */
export function SelectCheckboxCell<TData>({
  row,
  tableUid,
  hasSelectionLimit,
  maxSelections,
}: SelectCheckboxCellProps<TData>) {
  const {
    selectAll,
    toggledNodes,
    setToggledNodes,
    fireSelectionChange,
    selectionCount,
    indeterminateRowIds,
  } = useDataTableContext<TData>();

  const isRowSelected = selectAll
    ? !toggledNodes.has(row.id)
    : toggledNodes.has(row.id);
  const isIndeterminate = indeterminateRowIds?.has(row.id) ?? false;

  const isAtLimit =
    hasSelectionLimit && selectionCount >= maxSelections! && !isRowSelected;
  const canSelect = row.getCanSelect() && !isAtLimit;

  return (
    <span role="presentation" onClick={(e) => e.stopPropagation()}>
      <Checkbox
        id={`${tableUid}-select-row-${row.id}`}
        labelText=""
        hideLabel
        checked={isRowSelected && !isIndeterminate}
        indeterminate={isIndeterminate}
        disabled={!canSelect}
        onChange={() => {
          if (!canSelect) return;
          setToggledNodes((prev) => {
            const next = new Set(prev);
            if (next.has(row.id)) {
              next.delete(row.id);
            } else {
              next.add(row.id);
            }
            fireSelectionChange(selectAll, next);
            return next;
          });
        }}
      />
    </span>
  );
}

// ─────────────────────────────────────
// Header component (select-all checkbox)
// ─────────────────────────────────────

interface SelectAllHeaderProps<TData> {
  table: Table<TData>;
  tableUid: string;
  isServerDriven: boolean;
}

/**
 * Header "select all" checkbox for the auto-injected `_select` column.
 * Branches on `isServerDriven`: server mode uses the AG-Grid `selectAll: true`
 * sentinel; client mode enumerates `table.getFilteredRowModel().rows` into
 * `toggledNodes` (client has all data, so the sentinel isn't needed).
 *
 * @param {SelectAllHeaderProps} props - Component props
 * @param {Table} props.table - TanStack table instance (for filtered-row enumeration)
 * @param {string} props.tableUid - Unique per-DataTable instance prefix for the checkbox `id`
 * @param {boolean} props.isServerDriven - Server vs. client selection semantics
 * @return {JSX.Element} The header checkbox element
 */
export function SelectAllHeader<TData>({
  table,
  tableUid,
  isServerDriven,
}: SelectAllHeaderProps<TData>) {
  const {
    selectAll,
    toggledNodes,
    setSelectAll,
    setToggledNodes,
    fireSelectionChange,
  } = useDataTableContext<TData>();

  let isChecked: boolean;
  let isIndeterminate: boolean;
  if (isServerDriven) {
    isChecked = selectAll && toggledNodes.size === 0;
    isIndeterminate = toggledNodes.size > 0;
  } else {
    // Client: count exceptions present in current filtered rows. Accounts
    // for selections outside the current filter without false positives. Only
    // SELECTABLE rows count — disabled/locked rows (`_canSelect === false`) are
    // never selected, so including them would keep the header stuck indeterminate.
    const selectableRows = table
      .getFilteredRowModel()
      .rows.filter((r) => r.getCanSelect());
    const selectedInFiltered = selectableRows.filter((r) =>
      toggledNodes.has(r.id)
    ).length;
    isChecked =
      selectableRows.length > 0 && selectedInFiltered === selectableRows.length;
    isIndeterminate =
      selectedInFiltered > 0 && selectedInFiltered < selectableRows.length;
  }

  return (
    <Checkbox
      id={`${tableUid}-select-all`}
      labelText=""
      hideLabel
      checked={isChecked}
      indeterminate={isIndeterminate}
      disabled={!table.getRowModel().rows.some((r) => r.getCanSelect())}
      onChange={(_evt, { checked }: { checked: boolean }) => {
        if (isServerDriven) {
          setSelectAll(checked);
          setToggledNodes(new Set());
          fireSelectionChange(checked, new Set());
        } else {
          // Client: enumerate SELECTABLE filtered row IDs into toggledNodes
          // (selectAll stays false — the sentinel isn't used in client mode).
          // Skip disabled/locked rows (`_canSelect === false`) so Select All never
          // selects a row the user can't individually select.
          const next = checked
            ? new Set(
                table
                  .getFilteredRowModel()
                  .rows.filter((r) => r.getCanSelect())
                  .map((r) => r.id)
              )
            : new Set<string>();
          setToggledNodes(next);
          fireSelectionChange(false, next);
        }
      }}
    />
  );
}
